/**
 * SubscriptionStatusChip — at-a-glance subscription state pill for coach
 * profile headers (Lead Detail + Athlete Dossier).
 *
 * Reads /api/coach/clients/{user_id}/subscription-status (Spotify-Family
 * billing aware — subs are per-athlete now) and renders one of:
 *
 *    🟢 ACTIVE SUB · $X/wk · Renews Jul 12          — active or trialing
 *    🟠 CANCEL-AT-PERIOD-END · Ends Jul 12          — sub still active but flagged
 *    🟡 PACKS ONLY                                  — no sub, has session credits
 *    ⚪ NO MEMBERSHIP                               — nothing on file
 *
 * Props:
 *   userId         (required) — the parent/billing user_id to inspect.
 *   athleteId      (optional) — if set, filter to just THIS athlete's sub.
 *                              Used on the Athlete Dossier header so each
 *                              kid shows their own sub state.
 *
 * Sachin 2026-02-27 — "Is there an easy indicator at the top of each
 * profile to show if that profile has a subscription?" — answer: now yes.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { BadgeCheck, AlertTriangle, Ticket, Circle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalise per-period cents to weekly for display. Mirrors the math in
// routes/_sub_pricing.py and routes/recurring_revenue.py so the chip
// agrees with the dashboard widgets.
const TO_WEEKLY = { day: 7.0, week: 1.0, month: 12.0 / 52.0, year: 1.0 / 52.0 };

function weeklyCentsFromSub(sub) {
    const base = Number(sub.effective_price_cents ?? sub.price_cents ?? 0);
    const ic = Math.max(1, Number(sub.interval_count || 1));
    const per_interval = base / ic;
    const factor = TO_WEEKLY[sub.interval || "month"] || 1.0;
    return Math.round(per_interval * factor);
}

function formatRenewDate(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return null;
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch { return null; }
}

export default function SubscriptionStatusChip({ userId, athleteId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId) { setLoading(false); return; }
        let alive = true;
        (async () => {
            try {
                const r = await axios.get(
                    `${API}/coach/clients/${userId}/subscription-status`,
                    { withCredentials: true },
                );
                if (alive) setData(r.data);
            } catch {
                if (alive) setData({ active: false, subscriptions: [] });
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [userId]);

    if (loading) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.2em] uppercase border border-zinc-800 text-zinc-500">
                <Loader2 className="w-3 h-3 animate-spin" /> Sub…
            </span>
        );
    }
    if (!data) return null;

    // Apply the athlete filter when present (Spotify-Family: subs are per
    // athlete_id; the dossier wants only that one kid's row).
    let subs = data.subscriptions || [];
    if (athleteId) {
        subs = subs.filter((s) => s.athlete_id === athleteId);
    }

    // ----- ACTIVE / TRIALING -----
    const active = subs.find((s) => s.status === "active" || s.status === "trialing");
    if (active) {
        const cancelling = !!active.cancel_at_period_end;
        const weeklyCents = weeklyCentsFromSub(active);
        const renewLabel = formatRenewDate(active.current_period_end);

        if (cancelling) {
            return (
                <span
                    data-testid="sub-chip-cancelling"
                    className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.2em] uppercase border border-amber-700/60 bg-amber-950/40 text-amber-200"
                    title={`Ends ${renewLabel || "soon"}`}
                >
                    <AlertTriangle className="w-3 h-3" />
                    Cancel at period end
                    {renewLabel && <span className="opacity-70 normal-case tracking-normal">· Ends {renewLabel}</span>}
                </span>
            );
        }
        return (
            <span
                data-testid="sub-chip-active"
                className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.2em] uppercase border border-emerald-600/60 bg-emerald-950/40 text-emerald-200"
                title={renewLabel ? `Renews ${renewLabel}` : "Active subscription"}
            >
                <BadgeCheck className="w-3 h-3" />
                Active sub
                {weeklyCents > 0 && (
                    <span className="opacity-90 normal-case tracking-normal">
                        · ${(weeklyCents / 100).toFixed(weeklyCents % 100 === 0 ? 0 : 2)}/wk
                    </span>
                )}
                {renewLabel && (
                    <span className="opacity-70 normal-case tracking-normal">· Renews {renewLabel}</span>
                )}
            </span>
        );
    }

    // ----- PAST DUE -----
    const pastDue = subs.find((s) => s.status === "past_due");
    if (pastDue) {
        return (
            <span
                data-testid="sub-chip-past-due"
                className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.2em] uppercase border border-rose-700/60 bg-rose-950/40 text-rose-200"
                title="Last invoice failed — card needs attention"
            >
                <AlertTriangle className="w-3 h-3" /> Past due
            </span>
        );
    }

    // ----- PACKS ONLY (member status but no sub) -----
    // If the user-status is "member" or "lead" with no sub but they're
    // identified as a member elsewhere, show packs-only. We don't have a
    // pack-count here; the chip is just a hint to click into billing.
    if (data.user_status === "member") {
        return (
            <span
                data-testid="sub-chip-packs-only"
                className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.2em] uppercase border border-yellow-700/60 bg-yellow-950/40 text-yellow-200"
                title="No sub on file; check session-packs balance"
            >
                <Ticket className="w-3 h-3" /> Packs only
            </span>
        );
    }

    // ----- NO MEMBERSHIP -----
    return (
        <span
            data-testid="sub-chip-none"
            className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.2em] uppercase border border-zinc-800 text-zinc-500"
            title="No active subscription"
        >
            <Circle className="w-3 h-3" /> No membership
        </span>
    );
}
